import 'vite/modulepreload-polyfill'; // absolute-first

// Don't move the following 2 imports from this order
import { ThemeProvider } from '@mui/material';
import { styled } from '@mui/material/styles';
// -----
//import '/@/styles/variables.css';
import * as Sentry from '@sentry/browser';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';

import React from 'react';
// -----

import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import ErrorBoundary from '/@/components/error-boundary';
import createTheme from '/@/components/theme-provider';
import settings from '/@/core/settings';
import configureLocale from '/@/locale';
import configureStore from '/@/store';
import colors, { typography } from './components/theme-provider/colors';
import { Mixpanel } from './core/mixpanel';
import '/@/styles/index.css';
import { createRouter } from '/@/router';
import { captureOverrideTheme } from '/@/utils/theme-override';

// Capture `?overrideTheme=...` before any client-side navigation can strip it from the URL.
captureOverrideTheme();

const initialize = async () => {
    if (window.SENTRY_PUBLIC_DSN) {
        Sentry.init({
            environment: window.ENVIRONMENT,
            release: window.VERSION,
            dsn: window.SENTRY_PUBLIC_DSN,
        });
    }

    if ('serviceWorker' in navigator && import.meta.env.PROD) {
        window.addEventListener('load', () => {
            // TODO: add service worker
            navigator.serviceWorker.register('/serviceworker.js');
        });

        // Hack to load new version ASAP.
        // Because the serviceworker is configured with skipWaiting === true,
        // whenever a new version is deployed the serviceworker is activated immediately.
        // But by the time the new serviceworker is activated the page already loaded with the previous version,
        // so we manually reload the page when we receive a controllerchange event and this time we load the new version.
        // This is hackish because the user might already be interacting with the app,
        // but this usually happens fast enough so we assume the user couldn't have done much yet.
        // navigator.serviceWorker.addEventListener('controllerchange', () => {
        //     window.location.reload();
        // });
    }

    const savedLanguage = localStorage.getItem('language-setted');
    if (
        savedLanguage &&
        window.INITIAL_SESSION.language !== savedLanguage &&
        ['es', 'en', 'pt'].includes(savedLanguage)
    ) {
        await configureLocale(savedLanguage);
    } else {
        await configureLocale(window.INITIAL_SESSION.language);
    }

    // TODO: show <Loading> while settings are loaded
    await settings.load(window.SETTINGS_URL);

    function updateFontBasedOnSettings() {
        document.documentElement.style.setProperty('--primary-font', typography.themes[settings.app.theme].fontFamily);
        document.body.style.fontFamily = 'var(--primary-font)';
    }

    updateFontBasedOnSettings();

    try {
        Mixpanel.init(window.MIXPANEL_TOKEN, {
            debug: window.MIXPANEL_DEBUG_MODE,
            track_pageview: 'full-url',
            persistence: 'localStorage',
            persistence_name: 'session',
            opt_out_tracking_by_default: true,
            record_sessions_percent: 100, // TODO: Make it lower after debug
            record_network: true,
            record_network_options: {
                ignoreRequestFn: (data) => !/^https?:\/\/api\.[^/?#]+\.com/.test(data.url),
            },
            record_mask_all_text: false,
            record_mask_text_selector: '.mp-mask, [data-private]',
            record_heatmap_data: true,
            autocapture: {
                pageview: 'full-url',
                click: true,
                input: true,
                scroll: true,
                submit: true,
                capture_text_content: false,
            },
        });
        Mixpanel.register({ app: settings.app.name });
        Mixpanel.people.set({ lastApp: settings.app.name });
    } catch (error) {
        console.log(error);
    }

    const store = configureStore();

    const muiTheme = createTheme({ app: settings.app.theme }); // Use our extended theme

    const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
        '&.notistack-MuiContent-success': {
            backgroundColor: colors.themes[settings.app.theme].success?.default,
        },
        '&.notistack-MuiContent-error': {
            backgroundColor: colors.themes[settings.app.theme].error?.default,
        },
    }));

    // Create router with context providers
    const router = createRouter();

    ReactDOM.render(
        <React.StrictMode>
            <Provider store={store}>
                <ThemeProvider theme={muiTheme}>
                    <SnackbarProvider
                        hideIconVariant
                        className='mp-mask'
                        style={{ fontSize: '12px' }}
                        Components={{
                            success: StyledMaterialDesignContent,
                            error: StyledMaterialDesignContent,
                        }}
                    >
                        <ErrorBoundary>
                            <RouterProvider router={router} />
                        </ErrorBoundary>
                    </SnackbarProvider>
                </ThemeProvider>
            </Provider>
        </React.StrictMode>,
        document.getElementById('root')
    );
};

initialize();
